import { rtkApi, GET_DASHBOARD } from '@/shared/api/rtkApi';
import { LocalStorageService } from '@/shared/services/localStorage.service';
import {
  ScormScoResponse,
  ScormScoParams,
  ScormDropOffResponse,
  ScormComparisonResponse,
  GeneralSummaryResponse,
  GeneralSummaryParams,
  GeneralMetricsResponse,
  GeneralMetricsParams,
  StudentSummaryResponse,
  StudentSummaryParams,
  StudentListResponse,
  StudentListParams,
  CourseSummaryResponse,
  CourseSummaryParams,
  TopCoursesResponse,
  TopCoursesParams,
  CourseAnalyticsResponse,
  CourseAnalyticsParams,
  CourseRetentionResponse,
  CourseRetentionParams,
  CoursePerformanceResponse,
  CoursePerformanceParams,
  LecturerSummaryResponse,
  LecturerSummaryParams,
  LecturerPerformanceResponse,
  LecturerPerformanceParams,
  AssignmentsSummaryResponse,
  AssignmentsSummaryParams,
  AssignmentsPerformanceResponse,
  AssignmentsPerformanceParams,
  CertificatesAchievementsSummaryResponse,
  CertificatesAchievementsSummaryParams,
  DashboardExportParams,
} from './types';

export const dashboardApi = rtkApi.injectEndpoints({
  endpoints: (build) => ({
    getGeneralSummary: build.query<GeneralSummaryResponse, GeneralSummaryParams>({
      query: (params) => ({
        url: '/admin/dashboard/general/summary',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getGeneralMetrics: build.query<GeneralMetricsResponse, GeneralMetricsParams>({
      query: (params) => ({
        url: '/admin/dashboard/general/metrics',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getStudentSummary: build.query<StudentSummaryResponse, StudentSummaryParams>({
      query: (params) => ({
        url: '/admin/dashboard/students/summary',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getStudentList: build.query<StudentListResponse, StudentListParams>({
      query: (params) => ({
        url: '/admin/dashboard/students/list',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getCourseSummary: build.query<CourseSummaryResponse, CourseSummaryParams>({
      query: (params) => ({
        url: '/admin/dashboard/courses/summary',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getTopCourses: build.query<TopCoursesResponse, TopCoursesParams>({
      query: (params) => ({
        url: '/admin/dashboard/courses/top',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getCourseAnalytics: build.query<CourseAnalyticsResponse, CourseAnalyticsParams>({
      query: (params) => ({
        url: '/admin/dashboard/courses/analytics',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getCourseRetention: build.query<CourseRetentionResponse, CourseRetentionParams>({
      query: (params) => ({
        url: '/admin/dashboard/courses/retention',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getCoursePerformance: build.query<CoursePerformanceResponse, CoursePerformanceParams>({
      query: (params) => ({
        url: '/admin/dashboard/courses/performance',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getLecturerSummary: build.query<LecturerSummaryResponse, LecturerSummaryParams>({
      query: (params) => ({
        url: '/admin/dashboard/lecturers/summary',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getLecturerPerformance: build.query<LecturerPerformanceResponse, LecturerPerformanceParams>({
      query: (params) => ({
        url: '/admin/dashboard/lecturers/performance',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getAssignmentsSummary: build.query<AssignmentsSummaryResponse, AssignmentsSummaryParams>({
      query: (params) => ({
        url: '/admin/dashboard/assignments/summary',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getAssignmentsPerformance: build.query<
      AssignmentsPerformanceResponse,
      AssignmentsPerformanceParams
    >({
      query: (params) => ({
        url: '/admin/dashboard/assignments/performance',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getCertificatesAchievementsSummary: build.query<
      CertificatesAchievementsSummaryResponse,
      CertificatesAchievementsSummaryParams
    >({
      query: (params) => ({
        url: '/admin/dashboard/certificates-achievements/summary',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getScormSco: build.query<ScormScoResponse, ScormScoParams>({
      query: (params) => ({
        url: '/admin/dashboard/scorm/sco',
        params,
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getScormDropOff: build.query<ScormDropOffResponse, number>({
      query: (packageId) => ({
        url: '/admin/dashboard/scorm/drop-off',
        params: { packageId },
      }),
      providesTags: [GET_DASHBOARD],
    }),
    getScormComparison: build.query<ScormComparisonResponse, void>({
      query: () => ({
        url: '/admin/dashboard/scorm/comparison',
      }),
      providesTags: [GET_DASHBOARD],
    }),
  }),
});

const buildExportUrl = (path: string, params: DashboardExportParams) => {
  const apiUrl = typeof __API__ === 'string' ? __API__ : import.meta.env.VITE_API_URL || '';
  const query = new URLSearchParams();
  Object.entries(params).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      query.append(key, String(value));
    }
  });
  const queryString = query.toString();
  return `${apiUrl}${path}${queryString ? `?${queryString}` : ''}`;
};

const applyExtensionFromContentType = (fallbackFileName: string, contentType: string | null) => {
  const type = contentType?.split(';')[0]?.trim();
  const extension =
    type === 'application/zip' || type === 'application/x-zip-compressed'
      ? 'zip'
      : type === 'text/csv'
        ? 'csv'
        : type === 'application/pdf'
          ? 'pdf'
          : null;

  if (!extension) return fallbackFileName;

  return fallbackFileName.replace(/\.[^.]+$/, `.${extension}`);
};

const triggerBlobDownload = (blob: Blob, fileName: string) => {
  const objectUrl = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = objectUrl;
  link.download = fileName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(objectUrl);
};

export const fetchDashboardExportBlob = async (
  path: string,
  params: DashboardExportParams,
  fallbackFileName: string,
): Promise<{ blob: Blob; fileName: string }> => {
  const accessToken = LocalStorageService.getAccessToken();
  const headers = new Headers();
  if (accessToken) {
    headers.set('Authorization', `Bearer ${accessToken}`);
  }

  const response = await fetch(buildExportUrl(path, params), {
    method: 'GET',
    credentials: 'include',
    headers,
  });

  if (!response.ok) {
    throw new Error(`Export failed with status ${response.status}`);
  }

  const blob = await response.blob();
  const disposition = response.headers.get('Content-Disposition');
  const matched = disposition?.match(/filename="?([^"]+)"?/);
  const fileName =
    matched?.[1] ?? applyExtensionFromContentType(fallbackFileName, response.headers.get('Content-Type'));

  return { blob, fileName };
};

export const downloadDashboardExport = async (
  path: string,
  params: DashboardExportParams,
  fallbackFileName: string,
): Promise<void> => {
  const { blob, fileName } = await fetchDashboardExportBlob(path, params, fallbackFileName);

  triggerBlobDownload(blob, fileName);
};

export const DASHBOARD_EXPORT_PATHS = {
  general: '/admin/dashboard/general/export',
  students: '/admin/dashboard/students/export',
  courses: '/admin/dashboard/courses/export',
  lecturers: '/admin/dashboard/lecturers/export',
  assignments: '/admin/dashboard/assignments/export',
  certificatesAchievements: '/admin/dashboard/certificates-achievements/export',
  scorm: '/admin/dashboard/scorm/export',
} as const;

export const {
  useGetGeneralSummaryQuery,
  useGetGeneralMetricsQuery,
  useGetStudentSummaryQuery,
  useGetStudentListQuery,
  useGetCourseSummaryQuery,
  useGetTopCoursesQuery,
  useGetCourseAnalyticsQuery,
  useGetCourseRetentionQuery,
  useGetCoursePerformanceQuery,
  useGetLecturerSummaryQuery,
  useGetLecturerPerformanceQuery,
  useGetAssignmentsSummaryQuery,
  useGetAssignmentsPerformanceQuery,
  useGetCertificatesAchievementsSummaryQuery,
  useGetScormScoQuery,
  useGetScormDropOffQuery,
  useGetScormComparisonQuery,
} = dashboardApi;
